/**
 * Company: SPHMedia
 * Description: Filter for keyword section page
 */

import { ReactElement, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { RouteFactory } from "@app/routePaths";
import { Tag } from "@app/types/Cue";
import { KeywordButtonFactory } from "@components/Button/utlis/helpers";
import { KeywordButtonType } from "@components/Button/utlis/types";
import FollowKeywordModal from "@components/FollowKeyword/FollowKeywordModal";
import {
  EMPTY_NOTIFICATION,
  KEYWORD_ADDED_SUCCESSFUL_NOTIFICATION,
  KEYWORD_REACH_LIMIT_NOTIFICATION,
  KEYWORD_REMOVED_SUCCESSFUL_NOTIFICATION,
  KEYWORD_UPDATED_UNSUCCESSFUL_NOTIFICATION,
} from "@features/notifications/NotificationToasts/utlis/constants";
import useNotificationStore from "@features/notifications/store/useNotificationStore";
import { sectionRequests } from "@pages/Section/Section.server";
import useMyBtStore from "@store/useMyBtStore";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { CommonClassNameAndTestId } from "@typings/Common";
import { MYBT_REG_LIMIT, MYBT_SUB_LIMIT } from "@util/constant";
import { cn, gaEventTracker, mySPHOpenLogin } from "@util/helpers";
import { HttpStatusCode } from "axios";

import KeywordFilterTooltip from "./components/KeywordFilterTooltip";
import { KeywordLimitModalFactory } from "./utlis/helpers";
import { KeywordType } from "./utlis/types";
import { KeywordFilterFactory } from "./KeywordTypeFactory";

export type KeywordFilterProps = {
  /** Classname for the root element */
  rootClassName?: string;
  /** Classname for the tag element */
  tagClassName?: string;
  /** Classname for the icon element */
  iconClassName?: string;
  /** Keyword tag object  */
  tag: Tag;
  /** A boolean indicating whether the keyword is followed. */
  isFollowed?: boolean;
  /** A boolean indicating whether should tooltip disable. */
  disableTooltip?: boolean;
  /** A boolean indicating whether should border display. */
  disableBorder?: boolean;
  /** A boolean indicating whether tag is render with link. */
  isLinkenabled?: boolean;
  /** Classname for the tooltip arrow element */
  toolTipArrowClassName?: string;
  /** Classname for the tooltip element */
  toolTipClassName?: string;
  /** A boolean indicating whether is component added in keyword listing page. */
  isKeywordListing?: boolean;
  /** Type of the tag text */
  keywordType?: KeywordType;
  /** Type of the button */
  keywordButtonType?: KeywordButtonType;
  /** Classname for the button element  */
  buttonClassName?: string;
  /** extra function to be called on click */
  extraFunction?: () => void;
} & CommonClassNameAndTestId;

/**
 * KeywordFilter
 * Description: Display the keyword filter in the vertical section page
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function KeywordFilter({
  testId,
  tag,
  rootClassName,
  tagClassName,
  disableTooltip = false,
  disableBorder = false,
  isLinkenabled = true,
  toolTipArrowClassName,
  toolTipClassName,
  keywordType,
  keywordButtonType = KeywordButtonType.default,
  buttonClassName,
  iconClassName,
}: KeywordFilterProps): ReactElement {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isModalShown, setIsModalShown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState(false);
  const [method, setMethod] = useState("add");
  const _tag: Partial<Tag> = {
    name: tag.name,
    urlPath: tag.urlPath,
  };
  const tagDisplayName = _tag.name ? _tag.name : "";
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [userType, mysphw] = useOKTAUserStore((store) => [
    store.userInfo?.usertype,
    store.userInfo?.mysphw,
  ]);

  const [
    addTag,
    minusTag,
    followedTags,
    setIsLimitModalShown,
    isLimitModalShown,
  ] = useMyBtStore((store) => [
    store.followTag,
    store.unfollowTag,
    store.tags,
    store.setIsLimitModalShown,
    store.isLimitModalShown,
    store.setIsLoading,
    store.setMyBtMessage,
    store.setIsMyBtBannerShown,
  ]);

  const [
    setIsNotificationLoading,
    setIsNotificationDisplayed,
    setNotification,
  ] = useNotificationStore((store) => [
    store.setIsNotificationLoading,
    store.setIsNotificationDisplayed,
    store.setNotification,
  ]);

  const isTagFollowed =
    pathname !== "/mybt/intro" &&
    followedTags.some(
      (followedTag) => followedTag.name.toLowerCase() === tag.name.toLowerCase()
    );

  const handleClick = () => {
    if (userType === OKTAUserTypeEnum.ANONYMOUS) {
      mySPHOpenLogin();
    }

    if (
      userType === OKTAUserTypeEnum.REGISTERED ||
      userType === OKTAUserTypeEnum.SUBSCRIBER
    ) {
      const apiToCall = isTagFollowed ? handleMinusTag : handleAddTag;

      apiToCall();
    }
  };

  const handleAddTag = () => {
    setIsLoading(true);
    setErrorMessages(false);

    if (
      followedTags.length >= MYBT_SUB_LIMIT &&
      keywordButtonType === KeywordButtonType.version3 &&
      userType === OKTAUserTypeEnum.SUBSCRIBER
    ) {
      setIsNotificationDisplayed(true);
      setIsNotificationLoading(false);
      setNotification(KEYWORD_REACH_LIMIT_NOTIFICATION);
      return;
    }

    if (
      followedTags.length >=
      (userType === OKTAUserTypeEnum.SUBSCRIBER
        ? MYBT_SUB_LIMIT
        : MYBT_REG_LIMIT)
    ) {
      setIsLimitModalShown(true);
      setIsLoading(false);

      return;
    }

    if (keywordButtonType === KeywordButtonType.version3) {
      setIsNotificationDisplayed(true);
      setIsNotificationLoading(true);
      setNotification(EMPTY_NOTIFICATION);
    }

    const fetchAPI = async () => {
      const response = await sectionRequests.updateMyBtFollowedKeywords(
        userType,
        mysphw ?? "",
        [tag]
      );

      if (response.code === HttpStatusCode.InternalServerError) {
        setErrorMessages(true);
        setIsModalShown(true);

        if (keywordButtonType === KeywordButtonType.version3) {
          setIsNotificationLoading(false);
          setNotification(KEYWORD_UPDATED_UNSUCCESSFUL_NOTIFICATION);
        }
      }

      if (
        response.code === HttpStatusCode.Ok &&
        response.message === "SUCCESS" &&
        response.errors &&
        response.errors.length > 0
      ) {
        setErrorMessages(true);
        setIsModalShown(true);

        if (keywordButtonType === KeywordButtonType.version3) {
          setIsNotificationLoading(false);
          setNotification(KEYWORD_UPDATED_UNSUCCESSFUL_NOTIFICATION);
        }
      }

      if (
        response.code === HttpStatusCode.Ok &&
        response.message == "SUCCESS" &&
        (!response.errors || response.errors.length === 0)
      ) {
        addTag(tag);
        setIsModalShown(true);

        if (keywordButtonType === KeywordButtonType.version3) {
          setIsNotificationLoading(false);
          setNotification(KEYWORD_ADDED_SUCCESSFUL_NOTIFICATION);
        }

        gaEventTracker("mybt", "add", tag.name);
      }

      if (response.code === HttpStatusCode.TooManyRequests) {
        if (keywordButtonType === KeywordButtonType.version3) {
          setIsNotificationDisplayed(true);
          setIsNotificationLoading(false);
          setNotification(KEYWORD_REACH_LIMIT_NOTIFICATION);

          return;
        }

        setIsLimitModalShown(true);
      }

      if (pathname === RouteFactory.myBTIntro)
        navigate(RouteFactory.myBTOnboard);

      setIsLoading(false);
    };

    fetchAPI();
  };

  const handleMinusTag = () => {
    setIsLoading(true);

    if (keywordButtonType === KeywordButtonType.version3) {
      setIsNotificationDisplayed(true);
      setIsNotificationLoading(true);
      setNotification(EMPTY_NOTIFICATION);
    }

    const fetchAPI = async () => {
      const response = await sectionRequests.deleteMyBtFollowedKeywords(
        userType,
        mysphw ?? "",
        [
          (tag = {
            ...tag,
            id:
              followedTags.find(
                (followedTag) =>
                  followedTag.name.toLocaleLowerCase() ===
                  tag.name.toLocaleLowerCase()
              )?.id || "",
          }),
        ]
      );

      if (response.code === HttpStatusCode.InternalServerError) {
        setErrorMessages(true);
        setMethod("delete");
        setIsModalShown(true);

        if (keywordButtonType === KeywordButtonType.version3) {
          setIsNotificationLoading(false);
          setNotification(KEYWORD_UPDATED_UNSUCCESSFUL_NOTIFICATION);
        }
      }

      if (
        response.code === HttpStatusCode.Ok &&
        response.message == "SUCCESS"
      ) {
        minusTag(tag);
        gaEventTracker("mybt", "remove", tag.name);

        if (keywordButtonType === KeywordButtonType.version3) {
          setIsNotificationLoading(false);
          setNotification(KEYWORD_REMOVED_SUCCESSFUL_NOTIFICATION);
        }
      }

      setIsLoading(false);
    };

    fetchAPI();
  };

  const renderWithTag =
    isLinkenabled && tag.urlPath ? KeywordType.withTag : KeywordType.default;

  const keywordProps = {
    tag,
    tagDisplayName,
    disableBorder,
    tagClassName,
    keywordType: keywordType ? keywordType : renderWithTag,
  };

  const KeywordButton = KeywordButtonFactory({
    keywordButtonType,
  });

  const KeywordLimitModal = KeywordLimitModalFactory({
    keywordButtonType,
  });

  return (
    <div
      data-testid={testId}
      className={cn(
        rootClassName,
        pathname === RouteFactory.myBTOnboard && isTagFollowed
          ? "relative border-black bg-black text-white"
          : "relative",
        pathname === RouteFactory.myBTOnboard && !isTagFollowed
          ? "border-gray-850 bg-white"
          : ""
      )}
      onMouseEnter={() => {
        setIsTooltipOpen(true);
      }}
      onMouseLeave={() => {
        setIsTooltipOpen(false);
      }}
    >
      <div className="group relative">
        <div className="flex items-center">
          <KeywordFilterFactory {...keywordProps} handleClick={handleClick} />

          <KeywordButton
            handleClick={handleClick}
            isLoading={isLoading}
            isTagFollowed={isTagFollowed}
            keyword={tag}
            className={buttonClassName}
            iconClassName={iconClassName}
            isAnonymousUser={userType === OKTAUserTypeEnum.ANONYMOUS}
          />
        </div>

        {isTooltipOpen && !disableTooltip ? (
          <KeywordFilterTooltip
            toolTipClassName={toolTipClassName}
            toolTipArrowClassName={toolTipArrowClassName}
          />
        ) : null}
      </div>

      {isModalShown &&
      (isTagFollowed || errorMessages) &&
      keywordButtonType !== KeywordButtonType.version3
        ? createPortal(
            <FollowKeywordModal hasError={errorMessages} type={method} />,
            document.body
          )
        : null}

      <KeywordLimitModal
        onClose={() => {
          setIsLimitModalShown(false);
        }}
        isModalOpen={isLimitModalShown}
      />
    </div>
  );
}
